import cn from 'classnames';
import type { FC } from 'react';
import { useMemo } from 'react';
import type { RegisterOptions } from 'react-hook-form@latest';
import { Controller, FormProvider, useForm } from 'react-hook-form@latest';

import { _get, _isEmpty, _isNil, _notNil } from '@/littledash';

import type {
  DataTableColumn,
  DataTableColumnAddRequest,
  DataTableColumnApiId,
} from '@/components/UI/DataTable/DataTable.model';

import { uniqueNameValidatorFactory } from '@/components/UI/DataTable/toolbar/Menus/AddColumn/AddColumn.util';
import { getRegisterConfig } from '@/components/UI/FormFields/FormField.utils';
import { FormField, Select } from '@/components/UI/FormFields/Latest';
import { NumberInput } from '@/components/UI/FormFields/Latest/Number';
import { DateUtils } from '@/utils/Date.utils';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { decimalPlacesValidator } from '@/validators';

import { ErrorMessage } from '@hookform/error-message';

import styles from './EditDataTableTimestampBaselineRelativeColumn.module.scss';

interface EditDataTableTimestamoBaselineRelativeColumnForm {
  name: string;
  timestamp_baseline_id: DataTableColumnApiId;
  interval: number;
  interval_time_unit: 'MINUTES' | 'HOURS' | 'DAYS';
}

interface EditDataTableTimestamoBaselineColumnProps {
  formData: EditDataTableTimestamoBaselineRelativeColumnForm;
  columnId?: DataTableColumnApiId;
  columns: Array<DataTableColumn>;
  onSubmit: (data: DataTableColumnAddRequest) => Promise<void>;
  closeModal: () => void;
}

export const timeUnitOptions = [
  {
    label: 'Minutes',
    value: 'MINUTES',
  },
  {
    label: 'Hours',
    value: 'HOURS',
  },
  {
    label: 'Days',
    value: 'DAYS',
  },
];

export const EditDataTableTimestampBaselineRelativeColumn: FC<EditDataTableTimestamoBaselineColumnProps> = ({
  formData,
  columnId,
  columns,
  onSubmit,
  closeModal,
}) => {
  const uniqueColumnName = useMemo(
    () =>
      uniqueNameValidatorFactory(
        new Set(columns.filter((c) => c.id !== columnId).map(({ name }) => (name ?? '').trim()))
      ),
    [columnId, columns]
  );
  const { interval, interval_time_unit, timestamp_baseline_id } = formData;
  const timestampBaselineColumns = useMemo(
    () =>
      columns.reduce<Array<{ value: DataTableColumnApiId; label: string }>>((acc, col) => {
        if (col.type === 'timestampBaseline' && _notNil(col.name)) {
          acc.push({ value: col.id, label: col.name });
        }
        return acc;
      }, []),
    [columns]
  );
  const methods = useForm<EditDataTableTimestamoBaselineRelativeColumnForm>({
    mode: 'onChange',
    defaultValues: {
      ...formData,
      interval_time_unit: interval_time_unit ?? 'HOURS',
      interval: DateUtils.convertSecondsToUnit(interval, interval_time_unit),
      timestamp_baseline_id: _notNil(timestamp_baseline_id)
        ? timestamp_baseline_id
        : timestampBaselineColumns?.[0]?.value,
    },
  });
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = methods;

  const intervalFieldRules = useMemo((): RegisterOptions<
    EditDataTableTimestamoBaselineRelativeColumnForm,
    'interval'
  > => {
    const baseRules = getRegisterConfig({
      required: true,
      min: 0.1,
      max: 1490000,
    });
    const { validate: validateFromBase, ...restBaseRules } = baseRules;
    const baseValidate =
      validateFromBase !== undefined && typeof validateFromBase === 'object' && !Array.isArray(validateFromBase)
        ? { ...validateFromBase }
        : {};
    const mergedValidate = {
      ...baseValidate,
      decimalPlacesValidator: (value: number | string) =>
        decimalPlacesValidator('interval')(typeof value === 'string' ? Number(value) : value),
    };
    return {
      ...restBaseRules,
      validate: mergedValidate,
    } as RegisterOptions<EditDataTableTimestamoBaselineRelativeColumnForm, 'interval'>;
  }, []);

  const mapFormData = async (data: EditDataTableTimestamoBaselineRelativeColumnForm) => {
    const { interval, interval_time_unit, timestamp_baseline_id } = data;
    await onSubmit({
      type: 'timestampBaselineRelative',
      name: data.name,
      pk: {
        interval: DateUtils.convertUnitToSeconds(interval, interval_time_unit),
        interval_time_unit,
        timestamp_baseline_id,
      },
    });
    closeModal();
  };

  return (
    <ModalContainer size="narrow">
      <ModalHeader
        title={`${_isNil(columnId) ? 'Add' : 'Update'} Timestamp: PK Bleed`}
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
      />
      <FormProvider {...methods}>
        <form>
          <div className="pa3 w-100 bl b--moon-gray h-100">
            <div className={styles['data-table-formula-column-container']}>
              <div>
                <label htmlFor="name">Column name</label>
                <input
                  type="text"
                  data-testid="column-name-input"
                  {...register('name', {
                    required: 'This field is required',
                    maxLength: 255,
                    validate: { uniqueColumnName },
                  })}
                  id="name"
                  placeholder="Name"
                  className={cn({ input__error: _notNil(errors?.name) })}
                  style={{ marginBottom: 0 }}
                  autoFocus
                />
                <ErrorMessage
                  errors={errors}
                  name="name"
                  render={({ message }) => (
                    <small className="red db pt2" data-testid="column-name-error">
                      {message}
                    </small>
                  )}
                />
              </div>
              <div className="mt3">
                <div>
                  <Select
                    required
                    name="timestamp_baseline_id"
                    label="PK Dose column"
                    options={timestampBaselineColumns}
                  />
                </div>
              </div>
              <div className="mt3">
                <div className="flex justify-apart">
                  <FormField label="Time from dose" name="interval" required testPrefix="number">
                    <Controller
                      control={control}
                      name="interval"
                      rules={intervalFieldRules}
                      render={({ field: { onChange, value, name } }) => (
                        <NumberInput
                          id={name}
                          onChange={onChange}
                          value={value}
                          step="any"
                          className={_notNil(_get(errors, 'interval')) ? 'input__error' : ''}
                          style={{ marginBottom: 0 }}
                          autoFocus={true}
                          testId="number"
                        />
                      )}
                    />
                  </FormField>
                  <div className="pl2">
                    <Select required name="interval_time_unit" label="Unit" options={timeUnitOptions} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <ModalActions
            className="pa3 bt b--moon-gray"
            onSubmit={handleSubmit(mapFormData)}
            onCancel={closeModal}
            submitBtnText={_isNil(columnId) ? 'Add' : 'Update'}
            submitButtonProps={{ disabled: !_isEmpty(errors) || !isDirty || isSubmitting, submit: true }}
          />
        </form>
      </FormProvider>
    </ModalContainer>
  );
};
